import React from 'react';

import type { BasicDoc, Hit } from 'react-instantsearch-core';

import EnterIcon from '@atlaskit/icon-lab/core/arrow-curved-down-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import Lozenge, { type SemanticColor } from '@atlaskit/lozenge';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import Highlight from './highlight';
import { LinkItem } from './items';

const iconColorMapping: {
	Components: 'var(--ds-icon-accent-green)';
	Tokens: 'var(--ds-icon-accent-yellow)';
	Foundations: 'var(--ds-icon-accent-teal)';
	'Get started': 'var(--ds-icon-accent-purple)';
	Patterns: 'var(--ds-icon-accent-orange)';
} = {
	Components: token('color.icon.accent.green'),
	Tokens: token('color.icon.accent.yellow'),
	Foundations: token('color.icon.accent.teal'),
	'Get started': token('color.icon.accent.purple'),
	Patterns: token('color.icon.accent.orange'),
};

const statusMap: Record<string, { title: string; appearance: SemanticColor }> = {
	// for backward compat
	draft: {
		title: 'Draft',
		appearance: 'neutral',
	},
	// for backward compat
	'intent-to-deprecate': {
		title: 'Caution',
		appearance: 'warning',
	},
	deprecated: {
		title: 'Deprecated',
		appearance: 'danger',
	},
	alpha: {
		title: 'Early Access',
		appearance: 'warning',
	},
	// temporary - until new names are added to statuses.
	'open-alpha': {
		title: 'Early Access',
		appearance: 'warning',
	},
	// experimental - may not make sense.
	'closed-beta': {
		title: 'Beta',
		appearance: 'discovery',
	},
	beta: {
		title: 'Beta',
		appearance: 'discovery',
	},
};
type ResultItemProps = {
	onClick?: () => void;
	hit: Hit<BasicDoc & { status: { type: string } }>;
	indexName: keyof typeof iconColorMapping;
	resultIndex: number;
	posInSet: number;
	setSize: number;
	isKeyboardActive: boolean;
	listboxId: string;
	queryKey: string;
	resultRef?: (el: HTMLAnchorElement | null) => void;
};

const dotStyles = xcss({
	height: '8px',
	width: '8px',
	marginBlockStart: 'space.075',
	borderRadius: 'radius.full',
	flexShrink: 0,
});

const enterIconWrapperStyles = xcss({
	color: 'color.text.selected',
});

const iconWrapperStyles = xcss({
	width: '24px',
	height: '24px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

/**
 * __Result item__
 */
const ResultItem = ({
	indexName,
	hit,
	onClick,
	isKeyboardActive,
	resultIndex,
	posInSet,
	setSize,
	listboxId,
	resultRef,
}: ResultItemProps): React.JSX.Element => {
	let path = hit.path;
	const hasMatchInTitle =
		hit._highlightResult.title?.matchLevel && hit._highlightResult.title?.matchLevel !== 'none';
	let hasMatchInDescription =
		hit._highlightResult.description?.matchLevel &&
		hit._highlightResult.description?.matchLevel !== 'none';
	const hasMatchInContent =
		hit._highlightResult.content?.matchLevel && hit._highlightResult.content?.matchLevel !== 'none';

	if (
		indexName === 'Components' &&
		hasMatchInContent &&
		!hasMatchInTitle &&
		!hasMatchInDescription
	) {
		path = `${hit.path}/${hit.contentTab}`;
	}

	let showDescription = hasMatchInDescription;
	if (!hasMatchInDescription && !hasMatchInContent) {
		showDescription = true;
	}
	const status = hit.status && hit.status?.type ? statusMap[hit.status?.type] : null;

	return (
		<LinkItem
			to={path}
			onResultClick={onClick}
			isKeyboardActive={isKeyboardActive}
			resultIndex={resultIndex}
			optionId={`${listboxId}-option-${resultIndex}`}
			posInSet={posInSet}
			setSize={setSize}
			resultRef={resultRef}
			aria-labelledby={`${listboxId}-option-${resultIndex}-title`}
			aria-describedby={`${listboxId}-option-${resultIndex}-description ${listboxId}-option-${resultIndex}-contentMatch`}
		>
			<Inline space="space.100" alignBlock="center" spread="space-between">
				<Inline space="space.100">
					<Box xcss={dotStyles} style={{ backgroundColor: iconColorMapping[indexName] }} />
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
					<Stack space="space.100" xcss={xcss({ width: '100%' })}>
						<Inline space="space.100" spread="space-between">
							<Text
								weight="bold"
								color={isKeyboardActive ? 'color.text.selected' : 'color.text'}
								id={`${listboxId}-option-${resultIndex}-title`}
							>
								<Highlight hit={hit} attribute="title" />
							</Text>
							{status && <Lozenge appearance={status.appearance}> {status.title}</Lozenge>}
						</Inline>

						{showDescription && (
							<Text
								as="p"
								id={`${listboxId}-option-${resultIndex}-description`}
								color={isKeyboardActive ? 'color.text.selected' : 'color.text.subtle'}
							>
								<Highlight hit={hit} attribute="description" />
							</Text>
						)}
						{hasMatchInContent && (
							<Text
								as="p"
								id={`${listboxId}-option-${resultIndex}-contentMatch`}
								color={isKeyboardActive ? 'color.text.selected' : 'color.text.subtle'}
							>
								<Highlight hit={hit} attribute="content" />
							</Text>
						)}
					</Stack>
				</Inline>
				<Box as="span" xcss={iconWrapperStyles}>
					{isKeyboardActive ? (
						<Box as="span" xcss={enterIconWrapperStyles} aria-hidden="true">
							<EnterIcon label="" size="small" />
						</Box>
					) : (
						<ChevronRightIcon color={token('color.icon.subtle')} label="" size="small" />
					)}
				</Box>
			</Inline>
		</LinkItem>
	);
};

const _default_1: React.MemoExoticComponent<
	({
		indexName,
		hit,
		onClick,
		isKeyboardActive,
		resultIndex,
		posInSet,
		setSize,
		listboxId,
		resultRef,
	}: ResultItemProps) => React.JSX.Element
> = React.memo(ResultItem, (prev, next) => {
	// Keep row renders minimal during arrow navigation.
	// Skip re-render if it's the same item (same hit.id), same keyboard state, and same query (for highlights).
	const prevHit = prev.hit as any;
	const nextHit = next.hit as any;

	return (
		prevHit?.id === nextHit?.id &&
		prev.isKeyboardActive === next.isKeyboardActive &&
		prev.queryKey === next.queryKey
	);
});
export default _default_1;
